import { useCallback, useRef } from 'react';

/**
 * §1: «Tab и Enter переходят по полям, Enter в последнем поле создаёт новую строку». Tab already
 * works via native browser focus order (DOM order matches reading order in a `<table>`). This
 * hook only needs to intercept Enter: move to `[data-nav-row][data-nav-col+1]`, or the first cell
 * of the next row, or — on the very last nav-cell — call `onAddRow` and focus the new row's first
 * cell once it renders.
 */
export function useGridNavigation(onAddRow: () => void) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pendingFocusRow = useRef<number | null>(null);

  const focusRow = useCallback((row: number) => {
    const el = containerRef.current?.querySelector<HTMLElement>(`[data-nav-row="${row}"][data-nav-col="0"]`);
    el?.focus();
  }, []);

  const onKeyDownCapture = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      const target = e.target as HTMLElement;
      const cell = target.closest<HTMLElement>('[data-nav-row]');
      if (!cell || !containerRef.current) return;
      // Let dropdown/select components consume Enter for their own selection first.
      if (target.getAttribute('role') === 'combobox' || target.closest('.ant-select-open')) return;

      e.preventDefault();
      const row = Number(cell.dataset.navRow);
      const col = Number(cell.dataset.navCol);

      const sameRowNext = containerRef.current.querySelector<HTMLElement>(`[data-nav-row="${row}"][data-nav-col="${col + 1}"]`);
      if (sameRowNext) {
        sameRowNext.focus();
        return;
      }
      const nextRowFirst = containerRef.current.querySelector<HTMLElement>(`[data-nav-row="${row + 1}"][data-nav-col="0"]`);
      if (nextRowFirst) {
        nextRowFirst.focus();
        return;
      }
      pendingFocusRow.current = row + 1;
      onAddRow();
    },
    [onAddRow],
  );

  /** Call after a new row renders, to move focus into it (Enter-added row, §1). */
  const focusPendingRow = useCallback(() => {
    if (pendingFocusRow.current === null) return;
    const row = pendingFocusRow.current;
    pendingFocusRow.current = null;
    requestAnimationFrame(() => focusRow(row));
  }, [focusRow]);

  return { containerRef, onKeyDownCapture, focusPendingRow, focusRow };
}
